// src/features/dashboard/components/EmployeeActiveHoursReport/TimeUtilisationDonut.tsx

import {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';

import {
  TimeUtilisationResponse,
} from '../../types/EmployeeActiveHours/employeeActiveHours.types';

interface Props {
  data: TimeUtilisationResponse;
}

interface ChartItem {
  name: string;
  value: number;
  color: string;
}

interface TooltipState {
  visible: boolean;
  item: ChartItem | null;
  x: number;
  y: number;
}

interface SectorData {
  cx?: number;
  cy?: number;
  midAngle?: number;
  outerRadius?: number;
}

interface PieLabelProps {
  cx?: number;
  cy?: number;
  midAngle?: number;
  innerRadius?: number;
  outerRadius?: number;
  percent?: number;
}

const RADIAN = Math.PI / 180;

/* ========================================================================= */
/* PIE LABEL                                                                 */
/* ========================================================================= */

const renderLabel = ({
  cx = 0,
  cy = 0,
  midAngle = 0,
  innerRadius = 0,
  outerRadius = 0,
  percent = 0,
}: PieLabelProps) => {
  /*
   * Do not show labels for very small slices.
   */
  if (percent < 0.05) {
    return null;
  }

  const radius =
    innerRadius +
    (outerRadius - innerRadius) * 0.55;

  const x =
    cx +
    radius *
      Math.cos(-midAngle * RADIAN);

  const y =
    cy +
    radius *
      Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#FFFFFF"
      fontSize={12}
      fontWeight={700}
      textAnchor="middle"
      dominantBaseline="central"
      style={{
        pointerEvents: 'none',
      }}
    >
      {`${Math.round(percent * 100)}%`}
    </text>
  );
};

/* ========================================================================= */
/* COMPONENT                                                                 */
/* ========================================================================= */

const TimeUtilisationDonut = ({
  data,
}: Props) => {
  const cardRef =
    useRef<HTMLDivElement>(null);

  const chartWrapperRef =
    useRef<HTMLDivElement>(null);

  const [activeSlice, setActiveSlice] =
    useState<string | null>(null);

  const [tooltip, setTooltip] =
    useState<TooltipState>({
      visible: false,
      item: null,
      x: 0,
      y: 0,
    });

  /* ========================================================================= */
  /* CHART DATA                                                                */
  /* ========================================================================= */

  const chartData: ChartItem[] = [
    {
      name: 'Active',
      value: Math.max(
        0,
        Number(data.activePercent) || 0,
      ),
      color: '#7C4DDF',
    },
    {
      name: 'Meetings',
      value: Math.max(
        0,
        Number(data.meetingPercent) || 0,
      ),
      color: '#3B82F6',
    },
    {
      name: 'Idle',
      value: Math.max(
        0,
        Number(data.idlePercent) || 0,
      ),
      color: '#F59E0B',
    },
  ];

  const visibleData =
    chartData.filter(
      (item) => item.value > 0,
    );

  /* ========================================================================= */
  /* HIDE TOOLTIP                                                              */
  /* ========================================================================= */

  const hideTooltip = () => {
    setActiveSlice(null);

    setTooltip({
      visible: false,
      item: null,
      x: 0,
      y: 0,
    });
  };

  /* ========================================================================= */
  /* SHOW TOOLTIP INSIDE CARD                                                  */
  /* ========================================================================= */

  const showTooltipInsideCard = (
    item: ChartItem,
    preferredY = 112,
  ) => {
    const card =
      cardRef.current;

    if (!card) {
      return;
    }

    const cardWidth =
      card.clientWidth;

    const cardHeight =
      card.clientHeight;

    const tooltipWidth = 150;

    const tooltipHeight = 64;

    /*
     * Keep tooltip inside the right side
     * of the utilisation card.
     */
    let x =
      cardWidth -
      tooltipWidth -
      8;

    let y = preferredY;

    /*
     * Safety boundaries.
     */
    x = Math.max(
      8,
      Math.min(
        x,
        cardWidth -
          tooltipWidth -
          8,
      ),
    );

    y = Math.max(
      55,
      Math.min(
        y,
        cardHeight -
          tooltipHeight -
          8,
      ),
    );

    setActiveSlice(item.name);

    setTooltip({
      visible: true,
      item,
      x,
      y,
    });
  };

  /* ========================================================================= */
  /* PIE HOVER TOOLTIP                                                         */
  /* ========================================================================= */

  const calculateTooltipPosition = (
    sector: SectorData,
    item: ChartItem,
  ) => {
    /*
     * Idle should always float toward
     * the right side of the card.
     */
    if (item.name === 'Idle') {
      showTooltipInsideCard(
        item,
        112,
      );

      return;
    }

    const wrapper =
      chartWrapperRef.current;

    const card =
      cardRef.current;

    if (!wrapper || !card) {
      return;
    }

    const wrapperRect =
      wrapper.getBoundingClientRect();

    const cardRect =
      card.getBoundingClientRect();

    const cx =
      Number(sector.cx) || 0;

    const cy =
      Number(sector.cy) || 0;

    const outerRadius =
      Number(
        sector.outerRadius,
      ) || 90;

    const midAngle =
      Number(
        sector.midAngle,
      ) || 0;

    const tooltipWidth = 150;

    const tooltipHeight = 64;

    const angle =
      -midAngle * RADIAN;

    const distance =
      outerRadius + 16;

    let x =
      wrapperRect.left -
      cardRect.left +
      cx +
      distance *
        Math.cos(angle);

    let y =
      wrapperRect.top -
      cardRect.top +
      cy +
      distance *
        Math.sin(angle);

    x -= tooltipWidth / 2;

    y -= tooltipHeight / 2;

    /*
     * Keep tooltip inside card.
     */
    const minX = 8;

    const maxX =
      cardRect.width -
      tooltipWidth -
      8;

    const minY = 55;

    const maxY =
      cardRect.height -
      tooltipHeight -
      8;

    x = Math.max(
      minX,
      Math.min(
        x,
        maxX,
      ),
    );

    y = Math.max(
      minY,
      Math.min(
        y,
        maxY,
      ),
    );

    setActiveSlice(item.name);

    setTooltip({
      visible: true,
      item,
      x,
      y,
    });
  };

  /* ========================================================================= */
  /* PIE ENTER                                                                 */
  /* ========================================================================= */

  const handlePieEnter = (
    sector: unknown,
    index: number,
  ) => {
    const item =
      visibleData[index];

    if (!item) {
      return;
    }

    calculateTooltipPosition(
      sector as SectorData,
      item,
    );
  };

  /* ========================================================================= */
  /* LEGEND ENTER                                                              */
  /* ========================================================================= */

  const handleLegendEnter = (
    name: string,
  ) => {
    const item =
      visibleData.find(
        (entry) =>
          entry.name === name,
      );

    if (!item) {
      return;
    }

    showTooltipInsideCard(
      item,
      112,
    );
  };

  /* ========================================================================= */
  /* RESIZE                                                                    */
  /* ========================================================================= */

  useEffect(() => {
    const handleResize = () => {
      hideTooltip();
    };

    window.addEventListener(
      'resize',
      handleResize,
    );

    return () => {
      window.removeEventListener(
        'resize',
        handleResize,
      );
    };
  }, []);

  /* ========================================================================= */
  /* SLICE OPACITY                                                             */
  /* ========================================================================= */

  const getSliceOpacity = (
    name: string,
  ) => {
    if (!activeSlice) {
      return 1;
    }

    return activeSlice === name
      ? 1
      : 0.25;
  };

  /* ========================================================================= */
  /* RENDER                                                                    */
  /* ========================================================================= */

  return (
    <div
      ref={cardRef}
      className="
        relative
        flex
        h-full
        w-full
        min-w-0
        flex-col
        overflow-visible
        rounded-[16px]
        border
        border-[#D0D5DD]
        bg-white
        p-4
      "
    >
      {/* ===================================================================== */}
      {/* HEADER                                                                 */}
      {/* ===================================================================== */}

      <div
        className="
          mb-0
          flex
          min-w-0
          items-center
          gap-2
          whitespace-nowrap
        "
      >
        <h3
          className="
            shrink-0
            text-[15px]
            font-semibold
            text-[#002B5B]
          "
        >
          Time Utilisation
        </h3>

        <span
          className="
            min-w-0
            truncate
            rounded-full
            bg-[#EEF4FF]
            px-3
            py-[4px]
            text-[10px]
            font-medium
            text-[#2563EB]
          "
        >
          Of {data.totalLoggedHours} logged today
        </span>
      </div>

      {/* ===================================================================== */}
      {/* DONUT CHART                                                            */}
      {/* ===================================================================== */}

      <div
        ref={chartWrapperRef}
        className="
          relative
          mx-auto
          flex
          h-[225px]
          w-full
          max-w-[280px]
          items-center
          justify-center
          overflow-visible
        "
      >
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <PieChart
            margin={{
              top: 5,
              right: 5,
              bottom: 5,
              left: 5,
            }}
          >
            <Pie
              data={visibleData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={62}
              outerRadius={94}
              paddingAngle={2}
              stroke="#FFFFFF"
              strokeWidth={2}
              label={renderLabel}
              labelLine={false}
              isAnimationActive
              animationDuration={700}
              onMouseEnter={
                handlePieEnter
              }
            >
              {visibleData.map(
                (
                  entry,
                  index,
                ) => (
                  <Cell
                    key={`${entry.name}-${index}`}
                    fill={entry.color}
                    fillOpacity={getSliceOpacity(
                      entry.name,
                    )}
                    stroke="#FFFFFF"
                    strokeWidth={
                      activeSlice ===
                      entry.name
                        ? 3
                        : 2
                    }
                    style={{
                      cursor:
                        'pointer',
                      outline:
                        'none',
                      transition:
                        'opacity 180ms ease, stroke-width 180ms ease',
                    }}
                  />
                ),
              )}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* =================================================================== */}
        {/* CENTER TEXT                                                          */}
        {/* =================================================================== */}

        <div
          className="
            pointer-events-none
            absolute
            left-1/2
            top-1/2
            flex
            -translate-x-1/2
            -translate-y-1/2
            flex-col
            items-center
            justify-center
            text-center
          "
        >
          <p
            className="
              text-[12px]
              font-medium
              text-[#667085]
            "
          >
            Utilisation
          </p>

          <p
            className="
              mt-1
              text-[20px]
              font-bold
              leading-none
              text-[#101828]
            "
          >
            {Math.round(
              Number(
                data.utilization,
              ) || 0,
            )}
            %
          </p>
        </div>
      </div>

      {/* ===================================================================== */}
      {/* TOOLTIP                                                                */}
      {/* ===================================================================== */}

      {tooltip.visible &&
        tooltip.item && (
          <div
            className="
              pointer-events-none
              absolute
              z-[9999]
              w-[150px]
              rounded-[10px]
              border
              border-[#E5E7EB]
              bg-white
              px-3
              py-2.5
              shadow-[0_8px_24px_rgba(16,24,40,0.16)]
            "
            style={{
              left: tooltip.x,
              top: tooltip.y,
            }}
          >
            <div
              className="
                mb-1.5
                flex
                items-center
                gap-2
              "
            >
              <span
                className="
                  h-2.5
                  w-2.5
                  shrink-0
                  rounded-full
                "
                style={{
                  backgroundColor:
                    tooltip.item.color,
                }}
              />

              <span
                className="
                  text-[12px]
                  font-medium
                  text-[#344054]
                "
              >
                {tooltip.item.name}
              </span>
            </div>

            <div
              className="
                flex
                items-baseline
                gap-1
              "
            >
              <span
                className="
                  text-[18px]
                  font-bold
                  leading-none
                  text-[#101828]
                "
              >
                {Number(
                  tooltip.item.value,
                ).toFixed(1)}
                %
              </span>

              <span
                className="
                  whitespace-nowrap
                  text-[10px]
                  text-[#667085]
                "
              >
                of logged time
              </span>
            </div>
          </div>
        )}

      {/* ===================================================================== */}
      {/* LEGEND - ONE ROW                                                      */}
      {/* ===================================================================== */}

      <div
        className="
          mt-0
          flex
          w-full
          min-w-0
          items-center
          justify-center
          gap-x-2
          overflow-hidden
          whitespace-nowrap
          text-[11px]
        "
      >
        {visibleData.map(
          (item) => {
            const isActive =
              activeSlice ===
              item.name;

            return (
              <div
                key={item.name}
                onMouseEnter={() =>
                  handleLegendEnter(
                    item.name,
                  )
                }
                onMouseLeave={
                  hideTooltip
                }
                className={`
                  flex
                  min-w-0
                  shrink
                  items-center
                  gap-1
                  rounded-full
                  px-1
                  py-1
                  cursor-pointer
                  transition-all
                  duration-150
                  ${
                    isActive
                      ? 'bg-[#F2F4F7]'
                      : ''
                  }
                `}
              >
                {/* Color dot */}

                <span
                  className="
                    h-2.5
                    w-2.5
                    shrink-0
                    rounded-full
                  "
                  style={{
                    backgroundColor:
                      item.color,
                  }}
                />

                {/* Name */}

                <span
                  className="
                    shrink-0
                    text-[11px]
                    font-medium
                    text-[#344054]
                  "
                >
                  {item.name}
                </span>

                {/* Percentage */}

                <span
                  className="
                    shrink-0
                    text-[11px]
                    font-medium
                    text-[#344054]
                  "
                >
                  {Math.round(
                    item.value,
                  )}
                  %
                </span>
              </div>
            );
          },
        )}
      </div>
    </div>
  );
};

export default TimeUtilisationDonut;